import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Arrow, Slider } from '@tc-packages/tc-common'

const MOBILE_BREAKPOINT = 768

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT,
  )

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isMobile
}

const trackSlideClick = (slide, index) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event: 'homehero_select_promotion',
    ecommerce: {
      creative_name: 'homepage_hero',
      creative_slot: `slide_${index + 1}`,
      promotion_id: String(slide.id),
      promotion_name: slide.name,
    },
  })
}

const HeroButton = ({ button, onClick }) => {
  if (!button) return null

  return (
    <a
      className={`heroCarousel_link buttonComponent--${button.secondaryStyling ? 'secondary' : 'primary'}`}
      href={button.url}
      onClick={onClick}
      rel={button.newTab ? 'noopener noreferrer' : undefined}
      target={button.newTab ? '_blank' : undefined}
    >
      {button.title}
    </a>
  )
}

HeroButton.propTypes = {
  button: PropTypes.shape({
    newTab: PropTypes.bool,
    secondaryStyling: PropTypes.bool,
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  onClick: PropTypes.func,
}

const HeroImage = ({ slide, isMobile }) => {
  const [loaded, setLoaded] = useState(false)

  return (
    <picture
      className={`heroCarousel_media ${
        slide.useWhiteFont ? 'heroCarousel_media--whiteFont' : ''
      } ${loaded ? '' : 'heroCarousel_media--loading'}`}
    >
      {slide.mobileImage && (
        <source
          media={`(max-width: ${MOBILE_BREAKPOINT - 1}px)`}
          srcSet={slide.mobileImage}
        />
      )}
      <img
        alt={
          isMobile && slide.mobileImage
            ? slide.mobileImageAlt
            : slide.desktopImageAlt
        }
        className="heroCarousel_img"
        onError={() => setLoaded(true)}
        onLoad={() => setLoaded(true)}
        src={slide.desktopImage}
      />
    </picture>
  )
}

HeroImage.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  slide: PropTypes.shape({
    desktopImage: PropTypes.string,
    desktopImageAlt: PropTypes.string,
    mobileImage: PropTypes.string,
    mobileImageAlt: PropTypes.string,
    useWhiteFont: PropTypes.bool,
  }).isRequired,
}

const HeroSlide = ({ slide, isMobile, onClick }) => (
  <div className="heroCarousel_slide">
    <HeroImage isMobile={isMobile} slide={slide} />
    <div
      className={`heroCarousel_textBlock ${
        slide.useOpaqueBackground ? 'heroCarousel_textBlock--opaque' : ''
      } ${slide.useWhiteFont ? 'heroCarousel_textBlock--whiteFont' : ''}`}
    >
      {slide.title && (
        <h2
          className={`heroCarousel_title${slide.useWhiteFont ? ' heroCarousel_title--white' : ''}`}
        >
          {slide.title}
        </h2>
      )}
      {slide.subtitle && (
        <p
          className={`heroCarousel_subtitle${slide.useWhiteFont ? ' heroCarousel_subtitle--white' : ''}`}
        >
          {slide.subtitle}
        </p>
      )}
      <div className="heroCarousel_buttons">
        <HeroButton button={slide.button} onClick={onClick} />
      </div>
    </div>
    {slide.button && (
      <a
        aria-hidden="true"
        className="heroCarousel_slideLink"
        href={slide.button.url}
        onClick={onClick}
        rel={slide.button.newTab ? 'noopener noreferrer' : undefined}
        tabIndex={-1}
        target={slide.button.newTab ? '_blank' : undefined}
      />
    )}
  </div>
)

HeroSlide.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  slide: PropTypes.shape({
    button: PropTypes.object,
    subtitle: PropTypes.string,
    title: PropTypes.string,
    useOpaqueBackground: PropTypes.bool,
    useWhiteFont: PropTypes.bool,
  }).isRequired,
}

export const HomeHeroCarousel = ({ slides }) => {
  const isMobile = useIsMobile()
  const [currentSlide, setCurrentSlide] = useState(0)

  if (!slides || slides.length === 0) return null

  const singleSlide = slides.length === 1
  const useSecondaryStyling = slides[currentSlide]?.useSecondaryStyling

  const settings = {
    afterChange: setCurrentSlide,
    arrows: !singleSlide,
    autoplay: !singleSlide && !isMobile,
    autoplaySpeed: 5000,
    dots: !singleSlide,
    infinite: !singleSlide,
    nextArrow: <Arrow direction="next" />,
    pauseOnHover: true,
    prevArrow: <Arrow direction="prev" />,
    slidesToScroll: 1,
    slidesToShow: 1,
    responsive: [
      {
        breakpoint: MOBILE_BREAKPOINT,
        settings: {
          arrows: false,
          autoplay: false,
          dots: !singleSlide,
          draggable: !singleSlide,
          swipe: !singleSlide,
        },
      },
    ],
  }

  return (
    <div
      className={`homeHeroSection${useSecondaryStyling ? ' homeHeroSection--secondaryStyling' : ''}`}
    >
      <Slider
        items={slides.map((slide, index) => (
          <HeroSlide
            isMobile={isMobile}
            key={slide.id}
            onClick={() => trackSlideClick(slide, index)}
            slide={slide}
          />
        ))}
        {...settings}
      />
    </div>
  )
}

HomeHeroCarousel.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      useOpaqueBackground: PropTypes.bool,
      useWhiteFont: PropTypes.bool,
      useSecondaryStyling: PropTypes.bool,
      button: PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
        newTab: PropTypes.bool,
        secondaryStyling: PropTypes.bool,
      }),
      desktopImage: PropTypes.string,
      desktopImageAlt: PropTypes.string,
      mobileImage: PropTypes.string,
      mobileImageAlt: PropTypes.string,
    }),
  ).isRequired,
}

export default HomeHeroCarousel
